import {
    ReactElement,
    useRef,
    useState,
    Dispatch,
    FormEventHandler,
    forwardRef,
    SetStateAction,
    useCallback,
    useImperativeHandle,
    useMemo
} from 'react';
import { GeneralTeamSettingsSectionFormRef } from './general-team-settings-section';
import { Contact, Team } from '@/api';
import { useUpdateTeamAction } from '../actions';
import {
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UIText
} from '@/ui';
import {
    ContactData,
    EditableContactsList,
    ContactsList
} from '@/common/components';
import { sanitisePhone, uuidv4 } from '@/common/util';
import { useCompaniesTranslation } from '../hooks';

type TeamContactsSettingsSectionProps = { team: Team };

export function TeamContactsSettingsSection(
    props: TeamContactsSettingsSectionProps
): ReactElement {
    const { team } = props;

    const formRef = useRef<GeneralTeamSettingsSectionFormRef>(null);
    const updateTeam = useUpdateTeamAction({ teamId: team.id });

    const [isEditing, setIsEditing] = useState(false);

    function onSubmit(data: { contacts: Contact[] }) {
        const { contacts } = data;
        updateTeam
            .updateTeam({
                contacts
            })
            .then(() => {
                setIsEditing(false);
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <TeamContactsSettingsSectionForm
            canEdit={updateTeam.isAllowed}
            ref={formRef}
            initialContacts={team.contacts}
            arcEnabled={team.settings.arcEnabled}
            loading={updateTeam.loading}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            onSubmit={onSubmit}
        />
    );
}

type TeamContactsSettingsSectionFormProps = {
    canEdit: boolean;
    initialContacts: Contact[];
    arcEnabled: boolean;
    loading: boolean;
    isEditing: boolean;
    setIsEditing: Dispatch<SetStateAction<boolean>>;
    onSubmit: (data: { contacts: Contact[] }) => void;
};

type TeamContactsSettingsSectionFormRef = {
    resetForm: () => void;
};

const TeamContactsSettingsSectionForm = forwardRef<
    TeamContactsSettingsSectionFormRef,
    TeamContactsSettingsSectionFormProps
>((props, ref): ReactElement => {
    const {
        canEdit,
        initialContacts,
        arcEnabled,
        loading,
        isEditing,
        setIsEditing,
        onSubmit
    } = props;

    const { t } = useCompaniesTranslation();

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);
    const [contacts, setContacts] = useState<ContactData[]>(
        initialContacts.map((contact) => ({
            id: uuidv4(),
            isValid: false,
            ...contact
        }))
    );

    /**
     * Contacts
     */
    const checkContacts = useCallback(
        (contacts: ContactData[]): string => {
            const invalidContacts = contacts.filter(
                (contact) => !contact.isValid
            );
            if (invalidContacts.length > 0) {
                return t('shared.teams.contacts_error_incomplete');
            }

            if (arcEnabled && contacts.length === 0) {
                return t(
                    'components.team_contacts_settings_section.error_monitoring_requires_contact'
                );
            }

            return '';
        },
        [arcEnabled, t]
    );
    const contactsError = useMemo(() => {
        if (!showRealTimeErrors) {
            return '';
        }
        return checkContacts(contacts);
    }, [showRealTimeErrors, contacts, checkContacts]);

    function submitForm() {
        onSubmit?.({
            contacts: contacts.map((c) => {
                return {
                    name: c.name,
                    role: c.role,
                    phone: sanitisePhone(c.phone)
                };
            })
        });
    }

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkContacts(contacts)) {
            setShowRealTimeErrors(false);
            submitForm();
        }
    };

    function resetForm() {
        setContacts(
            initialContacts.map((contact) => ({
                id: uuidv4(),
                isValid: false,
                ...contact
            }))
        );
        setShowRealTimeErrors(false);
        setIsEditing(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    const sectionTitle = t('components.team_contacts_settings_section.title');

    return (
        <UISection>
            {isEditing ? (
                <EditableForm
                    sectionTitle={sectionTitle}
                    contacts={contacts}
                    setContacts={setContacts}
                    contactsError={contactsError}
                    loading={loading}
                    showRealTimeErrors={showRealTimeErrors}
                    canEdit={canEdit}
                    resetForm={resetForm}
                    handleSubmit={handleSubmit}
                />
            ) : (
                <NonEditableForm
                    sectionTitle={sectionTitle}
                    contacts={contacts}
                    onEditClick={() => {
                        setIsEditing(true);
                        setShowRealTimeErrors(false);
                    }}
                    canEdit={canEdit}
                />
            )}
        </UISection>
    );
});

function NonEditableForm(props: {
    sectionTitle: string;
    contacts: ContactData[];
    onEditClick: () => void;
    canEdit: boolean;
}): ReactElement {
    const { sectionTitle, contacts, onEditClick, canEdit } = props;

    const { t } = useCompaniesTranslation();
    return (
        <>
            <UISectionHeader
                title={sectionTitle}
                rightButtons={
                    canEdit
                        ? [
                              {
                                  text: t('shared.labels.edit'),
                                  variant: 'textDefault',
                                  type: 'button',
                                  onClick: onEditClick
                              }
                          ]
                        : []
                }
            />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <EscalationContactsDescription />

                    <div>
                        <ContactsList contacts={contacts} />
                    </div>
                </div>
            </UISectionContent>
        </>
    );
}

function EditableForm(props: {
    sectionTitle: string;
    contacts: ContactData[];
    setContacts: Dispatch<SetStateAction<ContactData[]>>;
    contactsError: string;
    loading: boolean;
    showRealTimeErrors: boolean;
    canEdit: boolean;
    resetForm: () => void;
    handleSubmit: FormEventHandler;
}): ReactElement {
    const {
        sectionTitle,
        contacts,
        setContacts,
        contactsError,
        loading,
        showRealTimeErrors,
        canEdit,
        resetForm,
        handleSubmit
    } = props;

    const { t } = useCompaniesTranslation();
    return (
        <form onSubmit={handleSubmit} noValidate>
            <UISectionHeader title={sectionTitle} />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <EscalationContactsDescription />

                    <div>
                        <EditableContactsList
                            contacts={contacts}
                            setContacts={setContacts}
                            showRealTimeErrors={showRealTimeErrors}
                            canEdit={canEdit}
                        />
                    </div>

                    {contactsError && (
                        <UIText type="error">{contactsError}</UIText>
                    )}
                </div>
            </UISectionContent>
            <UISectionFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: loading,
                        disabled: loading,
                        text: loading
                            ? t('shared.labels.saving')
                            : t('shared.labels.save')
                    },
                    {
                        text: t('shared.labels.cancel'),
                        type: 'button',
                        disabled: loading,
                        onClick: resetForm
                    }
                ]}
            />
        </form>
    );
}

function EscalationContactsDescription(): ReactElement {
    const { t } = useCompaniesTranslation();
    return (
        <UIText type="default2">
            {t('components.team_contacts_settings_section.explainer')}
        </UIText>
    );
}
